import React from "react";
import { Play, Pause, Bell } from "lucide-react";
import { usePlayer, PREVIEW_LABEL } from "@/context/PlayerContext";
import { Reveal } from "@/components/site/Reveal";
import { CountdownTimer } from "@/components/site/CountdownTimer";
import { mediaUrl, publicApi } from "@/lib/api";
import { toPlayable, fmtReleaseDate } from "@/lib/release";
import { scrollToId } from "@/components/site/SmoothScroll";
import { ShareButton } from "@/components/site/ShareButton";
import { WatchVideoButton } from "@/components/site/VideoModal";

const Artwork = ({ release }) => {
  const src = mediaUrl(release.artwork_url);
  if (src && !release.artwork_pending) {
    return <img src={src} alt={`${release.title} cover art`} className="w-full h-full object-cover" style={{ borderRadius: "var(--radius)" }} />;
  }
  return (
    <div className="frame w-full h-full grid place-items-center" style={{ backgroundColor: "var(--ink-raised)", borderRadius: "var(--radius)" }}>
      <div className="text-center">
        <img src="/icon-512.png" alt="" className="w-20 mx-auto opacity-70" />
        <p className="font-caps text-ash mt-3" style={{ fontSize: ".55rem" }}>Artwork Coming</p>
      </div>
    </div>
  );
};

const UpcomingCard = ({ release }) => {
  const player = usePlayer();
  const playable = toPlayable(release);
  const isActive = player.isActive(release.id);
  const playing = isActive && player.isPlaying;

  return (
    <div className="frame p-5 flex flex-col shrink-0 w-full sm:w-[380px]" style={{ backgroundColor: "var(--ink-raised)" }} data-testid={`upcoming-card-${release.id}`}>
      <div style={{ aspectRatio: "1 / 1" }}><Artwork release={release} /></div>
      <div className="flex items-center gap-3 mt-4">
        <span className="badge-type">{release.type}</span>
      </div>
      <h3 className="font-display foil-text mt-2" style={{ fontSize: "1.6rem", lineHeight: 1.05 }}>{release.title}</h3>
      {release.subtitle && <p className="font-script text-gold" style={{ fontSize: "1.5rem", lineHeight: 1 }}>{release.subtitle}</p>}
      <p className="font-caps text-gold mt-2" style={{ fontSize: ".58rem" }}>{fmtReleaseDate(release.release_at, { weekday: true })}</p>

      <div className="mt-4"><CountdownTimer releaseAt={release.release_at} /></div>

      {release.announcement && <p className="mt-4" style={{ color: "var(--parchment)", fontSize: ".9rem" }}>{release.announcement}</p>}

      <div className="mt-auto pt-5 flex flex-wrap gap-3">
        {release.presave && (
          <a className="btn-gold" style={{ padding: ".8rem 1.3rem", fontSize: ".72rem" }} href={release.presave} target="_blank" rel="noopener noreferrer" data-testid={`presave-${release.id}`}
             onClick={() => publicApi.post("/public/track/presave", { id: release.id }).catch(() => {})}>
            <Bell size={13} fill="currentColor" /> Pre-save
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        )}
        {playable && (
          <button className={release.presave ? "btn-ghost" : "btn-gold"} style={{ padding: ".7rem 1.1rem" }} title={`${PREVIEW_LABEL} preview`} onClick={() => (isActive ? player.toggle() : player.playTrack(playable, 0))} data-testid={`upcoming-preview-${release.id}`}>
            {playing ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />} {playing ? "Pause preview" : "Play preview"}
          </button>
        )}
        <WatchVideoButton release={release} className="btn-gold" style={{ padding: ".7rem 1.1rem" }} />
        {!release.presave && (
          <button className="btn-ghost" style={{ padding: ".7rem 1.1rem" }} onClick={() => scrollToId("connect")} data-testid={`notify-${release.id}`}><Bell size={13} /> Notify me</button>
        )}
        <ShareButton kind="album" id={release.id} title={`${release.title} — ${release.artist || "P. Frazy"}`} text="Pre-save now" testId={`share-upcoming-${release.id}`} />
      </div>
      {playable && <p className="mt-3" style={{ color: "var(--ash)", fontSize: ".72rem" }} data-testid={`upcoming-play-note-${release.id}`}>{PREVIEW_LABEL} preview — the full song unlocks on release day.</p>}
    </div>
  );
};

export const Upcoming = ({ upcoming = [] }) => {
  if (upcoming.length === 0) return null;
  return (
    <section id="upcoming" className="section-pad" style={{ backgroundColor: "var(--ink-raised)" }} data-testid="upcoming-section">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
        <Reveal>
          <p className="font-caps text-gold" style={{ fontSize: ".72rem" }}>Coming Soon</p>
          <h2 className="font-display foil-text mt-3" style={{ fontSize: "clamp(1.3rem, 5.2vw, 3.5rem)", lineHeight: 1.05, whiteSpace: "nowrap" }}>New Music from HNS Records</h2>
        </Reveal>
        <Reveal delay={0.05} className="mt-12 flex flex-col sm:flex-row gap-6 sm:overflow-x-auto sm:no-scrollbar sm:pb-4 lg:flex-wrap lg:overflow-visible">
          {upcoming.map((r) => <UpcomingCard key={r.id} release={r} />)}
        </Reveal>
      </div>
    </section>
  );
};
